/**
 * Phase J — /admin/media (Object Storage image library + retention control).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pendingAlt, setPendingAlt] = useState("");
  const [retentionScan, setRetentionScan] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (type) params.content_type = type;
    api.get("/admin/media", { params }).then((r) => setItems(r.data.items || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [q, type]);

  const onUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const f of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        if (pendingAlt.trim()) fd.append("alt_text", pendingAlt.trim());
        await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) {
        toast.error(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    setPendingAlt("");
    load();
  };

  const saveAlt = async (fid, current) => {
    const next = window.prompt("Alt text (accessibility + SEO):", current || "");
    if (next === null) return;
    try {
      await api.patch(`/admin/media/${fid}`, { alt_text: next });
      toast.success("Alt text updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const del = async (fid) => {
    if (!window.confirm("Soft-delete this image? The storage object stays, but it's hidden from the app.")) return;
    await api.post(`/admin/media/${fid}/delete`);
    load();
  };

  const copy = (url) => {
    navigator.clipboard.writeText(window.location.origin + url);
    toast.success("Copied URL to clipboard");
  };

  const scan = async () => {
    const r = await api.post("/admin/storage/scan-retention");
    setRetentionScan(r.data);
  };
  const apply = async () => {
    if (!window.confirm("Apply retention now?\n\n11-month users get a warning email (RESEND-gated).\n12-month users' files are soft-deleted.")) return;
    const r = await api.post("/admin/storage/apply-retention");
    toast.success(`Warnings: ${r.data.warnings_sent} · Files purged: ${r.data.files_purged}`);
    setRetentionScan(null);
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-ivy tracking-tight">Media</h1>
          <p className="text-sm text-stone mt-1">Emergent Object Storage image library. Copy the /api/media URL to use in blog / marketing.</p>
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden onChange={(e) => onUpload(e.target.files)} data-testid="admin-media-file-input" />
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Alt text for next upload (SEO + a11y)"
            value={pendingAlt}
            onChange={(e) => setPendingAlt(e.target.value)}
            className="border border-stone/40 bg-bone px-3 py-2 text-sm outline-none focus:border-ivy min-w-[280px]"
            data-testid="admin-media-alt-text-input"
          />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-ivy hover:bg-ivy/90 text-bone uppercase tracking-[0.18em] text-xs font-semibold py-3 px-5 disabled:opacity-50" data-testid="admin-media-upload-btn">
            {uploading ? "Uploading…" : "+ Upload images"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <input type="search" placeholder="Search filename…" value={q} onChange={(e) => setQ(e.target.value)} className="border border-stone/40 bg-bone px-3 py-2 text-sm outline-none focus:border-ivy min-w-[220px]" data-testid="admin-media-search" />
        <div className="flex border border-stone/40">
          {[["","All"],["image/jpeg","JPEG"],["image/png","PNG"],["image/webp","WEBP"],["image/gif","GIF"]].map(([v,l]) => (
            <button key={v || "all"} onClick={() => setType(v)} className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 ${type === v ? "bg-ivy text-bone" : "text-ink hover:bg-stone/10"}`} data-testid={`admin-media-filter-${v || "all"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {items.length === 0 && <div className="col-span-6 text-center text-stone py-10 italic">No images uploaded yet.</div>}
        {items.map((m) => (
          <div key={m.file_id} className="border border-brass/60 bg-bone" data-testid={`admin-media-row-${m.file_id}`}>
            <div className="w-full h-[128px] bg-bone-dim overflow-hidden flex items-center justify-center">
              <img src={m.url} alt={m.alt_text || m.original_filename} className="max-w-full max-h-full object-contain" />
            </div>
            <div className="p-2 space-y-1">
              <div className="text-[11px] text-ink truncate" title={m.original_filename}>{m.original_filename}</div>
              {!m.alt_text && (
                <button
                  onClick={() => saveAlt(m.file_id, m.alt_text)}
                  className="inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] font-semibold bg-crimson/10 text-crimson border border-crimson/40 hover:bg-crimson hover:text-bone"
                  data-testid={`admin-media-missing-alt-${m.file_id}`}
                >missing alt text · add</button>
              )}
              {m.alt_text && (
                <button
                  onClick={() => saveAlt(m.file_id, m.alt_text)}
                  className="block text-[10px] text-ink-soft italic truncate hover:text-ivy text-left w-full"
                  title={`Edit: ${m.alt_text}`}
                  data-testid={`admin-media-alt-${m.file_id}`}
                >alt: {m.alt_text}</button>
              )}
              <div className="text-[10px] text-stone tabular-nums">{Math.round(m.size / 1024)} KB · {(m.created_at || "").slice(0, 10)}</div>
              <div className="flex gap-2">
                <button onClick={() => copy(m.url)} className="text-[10px] uppercase tracking-[0.14em] text-brass hover:text-ivy" data-testid={`admin-media-copy-${m.file_id}`}>Copy URL</button>
                <button onClick={() => del(m.file_id)} className="ml-auto text-[10px] uppercase tracking-[0.14em] text-crimson hover:text-crimson/80" data-testid={`admin-media-delete-${m.file_id}`}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 border border-stone/40 bg-bone p-6" data-testid="admin-media-retention-panel">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Retention</div>
            <h2 className="mt-2 font-serif text-xl text-ivy tracking-tight">Warn at 11 months · Purge at 12 months of inactivity</h2>
            <p className="text-xs text-stone mt-1">Admin-triggered only. Emails RESEND-gated. Purge is soft-delete (storage never destroyed).</p>
          </div>
          <div className="flex gap-3">
            <button onClick={scan} className="border border-ivy text-ivy hover:bg-ivy hover:text-bone uppercase tracking-[0.18em] text-xs font-semibold py-2 px-4" data-testid="admin-retention-scan-btn">Preview scan</button>
            <button onClick={apply} className="bg-crimson hover:bg-crimson/90 text-bone uppercase tracking-[0.18em] text-xs font-semibold py-2 px-4" data-testid="admin-retention-apply-btn">Apply retention</button>
          </div>
        </div>
        {retentionScan && (
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="border border-brass/50 bg-brass/10 p-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Warning threshold (11 months)</div>
              <div className="mt-2 font-serif text-2xl text-ink tabular-nums" data-testid="admin-retention-warning-count">{retentionScan.candidates_for_warning.length}</div>
              <ul className="mt-3 text-xs text-ink-soft space-y-1">
                {retentionScan.candidates_for_warning.slice(0, 20).map((u) => (
                  <li key={u.user_id}>{u.email} · {u.file_count} files · last active {(u.last_activity_at || "").slice(0, 10)}</li>
                ))}
              </ul>
            </div>
            <div className="border border-crimson/60 bg-crimson/5 p-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-crimson">Purge threshold (12 months)</div>
              <div className="mt-2 font-serif text-2xl text-ink tabular-nums" data-testid="admin-retention-purge-count">{retentionScan.candidates_for_purge.length}</div>
              <ul className="mt-3 text-xs text-ink-soft space-y-1">
                {retentionScan.candidates_for_purge.slice(0, 20).map((u) => (
                  <li key={u.user_id}>{u.email} · {u.file_count} files · last active {(u.last_activity_at || "").slice(0, 10)}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
